const SPECTRAL_REV = [
  [13, 27, 110],
  [26, 58, 158],
  [21, 101, 192],
  [0, 151, 167],
  [0, 137, 123],
  [56, 142, 60],
  [130, 119, 23],
  [230, 81, 0],
  [191, 54, 12],
  [142, 0, 0],
  [106, 27, 154],
  [136, 14, 79],
];

// Returns [r, g, b] for a normalized value t in [0, 1].
// 0 = violet (low/slow), 1 = dark red (high/fast).
export function spectralColor(t) {
  return interpolate(SPECTRAL_REV, t);
}

function interpolate(palette, t) {
  t = Math.max(0, Math.min(1, t));
  const idx = t * (palette.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.min(lo + 1, palette.length - 1);
  const frac = idx - lo;
  return [
    Math.round(palette[lo][0] + (palette[hi][0] - palette[lo][0]) * frac),
    Math.round(palette[lo][1] + (palette[hi][1] - palette[lo][1]) * frac),
    Math.round(palette[lo][2] + (palette[hi][2] - palette[lo][2]) * frac),
  ];
}

// Single source of truth for the speed→color mapping shared by the FCD WMS
// legend, the WMS SPEED_STYLE parameter, the vector curvature layers, and the
// pixel-to-speed inversion. Sqrt gives perceptual lift in the slow-speed range;
// the ceiling means anything ≥ MAX_SPEED_KMH saturates at the spectrum end.
export const MAX_SPEED_KMH = 130;
export function speedToT(speed) {
  return Math.max(0, Math.min(1, Math.sqrt(speed / MAX_SPEED_KMH)));
}
export function colorForSpeed(speed) {
  return spectralColor(speedToT(speed));
}

// CSS linear-gradient with stops positioned at each breakpoint's relative
// speed (not evenly along the spectrum), so it matches what the WMS renders.
export function speedGradient(direction, speeds) {
  const minSpeed = speeds[0];
  const maxSpeed = speeds[speeds.length - 1];
  const range = maxSpeed - minSpeed;
  const stops = speeds.map((speed) => {
    const [r, g, b] = colorForSpeed(speed);
    const pos = range === 0 ? 0 : ((speed - minSpeed) / range) * 100;
    return `rgb(${r},${g},${b}) ${pos.toFixed(2)}%`;
  });
  return `linear-gradient(${direction}, ${stops.join(", ")})`;
}

// WMS SPEED_STYLE parameter value for given speed breakpoints.
// Format: steps,R1,G1,B1,speed1,R2,G2,B2,speed2,...
export function wmsSpeedStyle(speeds) {
  const parts = [speeds.length];
  for (const speed of speeds) {
    const [r, g, b] = colorForSpeed(speed);
    parts.push(r, g, b, speed);
  }
  return parts.join(",");
}

const _paletteCache = new WeakMap();
function paletteFor(breakpoints) {
  let palette = _paletteCache.get(breakpoints);
  if (!palette) {
    palette = breakpoints.map((speed) => {
      const [r, g, b] = colorForSpeed(speed);
      return { speed, r, g, b };
    });
    _paletteCache.set(breakpoints, palette);
  }
  return palette;
}

// Inverse of the FCD WMS speed renderer (trace-render SpeedColorScale): the
// server linearly interpolates RGB between adjacent breakpoint colors across
// integer speeds, so we recover speed by finding the breakpoint segment whose
// RGB segment passes closest to the sample, and lerping the speed by the same
// fraction.
export function interpolatedSpeedFromColor(rgb, breakpoints) {
  const palette = paletteFor(breakpoints);
  let minDist = Infinity;
  let bestSpeed = null;
  for (let i = 0; i < palette.length - 1; i++) {
    const a = palette[i];
    const b = palette[i + 1];
    const dr = b.r - a.r;
    const dg = b.g - a.g;
    const db = b.b - a.b;
    const segLenSq = dr * dr + dg * dg + db * db;
    const sr = rgb[0] - a.r;
    const sg = rgb[1] - a.g;
    const sb = rgb[2] - a.b;
    const t =
      segLenSq === 0
        ? 0
        : Math.max(0, Math.min(1, (sr * dr + sg * dg + sb * db) / segLenSq));
    const projDr = sr - t * dr;
    const projDg = sg - t * dg;
    const projDb = sb - t * db;
    const dist = projDr * projDr + projDg * projDg + projDb * projDb;
    if (dist < minDist) {
      minDist = dist;
      bestSpeed = a.speed + t * (b.speed - a.speed);
    }
  }
  return Math.round(bestSpeed);
}
